import React, { useState } from "react";
import { useAppSelector } from '../../app/hooks';
import strings, { selectLanguage } from '../../components/localize/localizeSlice';

interface OrderFormProps {
  onSubmit: () => void;
  onBack: () => void;
  alternateInvoiceData?: AlternateInvoiceData;
  setFormData?: (data: AlternateInvoiceData) => void;
  customerNote?: string;
  setCustomerNote?: (note: string) => void;
}

export interface AlternateInvoiceData {
  name: string;
  streetNumber: string;
  zip: string;
  city: string;
  country: string;
}

const OrderForm: React.FC<OrderFormProps> = ({ onSubmit , onBack, alternateInvoiceData, setFormData, customerNote, setCustomerNote}) => {
  useAppSelector(selectLanguage);
    const {priceCalculationState} = useAppSelector((state) => state.priceCalculator);
  

  const [sameAsCustomer, setSameAsCustomer] = useState(true);

  const handleInvoiceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!setFormData) return;

    var data = alternateInvoiceData || { name: "", streetNumber: "", zip: "", city: "", country: "" };
    data = { ...data, [e.target.name]: e.target.value } as AlternateInvoiceData;
    setFormData(data);
  };

  // Helper to check if all fields are filled for alternative recipient
  const isInvoiceComplete =  alternateInvoiceData ? Object.values(alternateInvoiceData).every((val) => val != null && val.trim() !== "") : false;

  return (
    <div className="p-6 mt-10 bg-white dark:bg-gray-800 shadow-md rounded-lg">
      {/* Rechnungsempfänger wie Auftraggeber */}
      <div className="mb-6">
        <label className="flex items-center dark:text-white">
          <input
            type="checkbox"
            checked={sameAsCustomer}
            onChange={() => setSameAsCustomer(!sameAsCustomer)}
            className="mr-2"
          />
          {strings.form.orderSummary.invoiceRecipientSameAsCustomer || "Rechnungsempfänger wie Auftraggeber"}
        </label>
      </div>

      {/* Alternative Rechnungsempfänger */}
      {!sameAsCustomer && (
        <div className="grid grid-cols-1 gap-6 mb-6">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left">
              {strings.form.orderSummary.name || "Name"}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={alternateInvoiceData?.name}
              onChange={handleInvoiceChange}
              placeholder={strings.form.orderSummary.name || "Name"}
              className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg"
            />
          </div>
          {/* Street and Number */}
          <div>
            <label htmlFor="streetNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left">
              {strings.form.orderSummary.streetNumber || "Straße und Hausnummer"}
            </label>
            <input
              type="text"
              id="streetNumber"
              name="streetNumber"
              value={alternateInvoiceData?.streetNumber}
              onChange={handleInvoiceChange}
              placeholder={strings.form.orderSummary.streetNumber || "Straße und Hausnummer"}
              className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg"
            />
          </div>
          {/* Zip */}
          <div>
            <label htmlFor="zip" className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left">
              {strings.form.orderSummary.zip || "PLZ"}
            </label>
            <input
              type="text"
              id="zip"
              name="zip"
              value={alternateInvoiceData?.zip}
              onChange={handleInvoiceChange}
              placeholder={strings.form.orderSummary.zip || "PLZ"}
              className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg"
            />
          </div>
          {/* City */}
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left">
              {strings.form.orderSummary.city || "Ort"}
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={alternateInvoiceData?.city}
              onChange={handleInvoiceChange}
              placeholder={strings.form.orderSummary.city || "Ort"}
              className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg"
            />
          </div>
          {/* Country */}
          <div>
            <label htmlFor="country" className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left">
              {strings.form.orderSummary.country || "Land"}
            </label>
            <input
              type="text"
              id="country"
              name="country"
              value={alternateInvoiceData?.country}
              onChange={handleInvoiceChange}
              placeholder={strings.form.orderSummary.country || "Land"}
              className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg"
            />
          </div>
        </div>
      )}

      {/* Customer Note */}
      <div className="mb-6">
        <label htmlFor="customerNote" className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left mb-2">
          {strings.form.orderSummary.customerNote || "Anmerkungen (optional)"}
        </label>
        <textarea
          id="customerNote"
          name="customerNote"
          rows={4}
          value={customerNote}
          onChange={(e) => setCustomerNote ? setCustomerNote(e.target.value) : null}
          placeholder={strings.form.orderSummary.customerNotePlaceholder || "Hier können Sie zusätzliche Informationen oder Wünsche für Ihren Auftrag hinterlassen..."}
          className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg resize-none"
        />
      </div>

      <button
        onClick={onSubmit}
        className={`w-full px-6 py-3 text-white rounded-md shadow-md focus:outline-none focus:ring-2 focus:ring-cuno-500 focus:ring-offset-2 dark:bg-cuno-500 dark:hover:bg-cuno-700
          ${!sameAsCustomer && !isInvoiceComplete ? "bg-gray-400 cursor-not-allowed" : "bg-cuno-500 hover:bg-cuno-700"}`}
        disabled={!sameAsCustomer && !isInvoiceComplete}
      >
        {Array.isArray(priceCalculationState) ? strings.form.orderSummary.submitOrder : strings.form.orderSummary.submitRequest}
      </button>

      <button
        onClick={onBack}
        className="w-full px-6 py-5 mt-5 text-gray-500 "
      >
        {strings.form.back}
      </button>
    </div>
  );
}

export default OrderForm;